<template>
  <div class="container-league">
    <b-row>
      <b-col cols="12" class="mb-2">
        <div
          v-if="!$route.params.leagueId"
          class="d-flex justify-content-between align-items-center"
        >
          <div class="d-flex align-items-center" style="gap: 16px">
            <b-button variant="outline-secondary" @click="handleBack">
              <feather-icon icon="ArrowLeftIcon" /> Back to Sports
            </b-button>
            <h4 class="font-weight-bolder mb-0">Add New League</h4>
          </div>

          <div>
            <b-button
              variant="outline-secondary"
              class="mr-1"
              @click="handleBack"
              >Cancel</b-button
            >
            <b-button
              @click="handleSubmitCreateLeague"
              :disabled="loadingCreateLeague"
              variant="success"
            >
              <feather-icon icon="PlusIcon" />
              <Spinner v-if="loadingCreateLeague" />
              <template v-else> Create League </template>
            </b-button>
          </div>
        </div>
        <div v-else class="d-flex justify-content-between align-items-center">
          <div class="d-flex align-items-center" style="gap: 16px">
            <b-button variant="outline-secondary" @click="handleBack">
              <feather-icon icon="ArrowLeftIcon" /> Back to Sports
            </b-button>
            <h4 class="font-weight-bolder mb-0" style="max-width: 400px">
              Edit League: {{ leagueItem.name }}
            </h4>
          </div>

          <div>
            <b-button
              variant="outline-secondary"
              class="mr-1"
              @click="handleBack"
              >Cancel</b-button
            >
            <b-button
              @click="handleSubmitUpdateLeague"
              :disabled="loadingCreateLeague"
              variant="success"
            >
              <feather-icon icon="SaveIcon" />
              <Spinner v-if="loadingCreateLeague" />
              <template v-else> Save Changes </template>
            </b-button>
          </div>
        </div>
      </b-col>

      <b-col cols="12">
        <b-card no-body class="mb-4">
          <b-card-header class="bg-dark text-white p-1">
            🏆 League Management
          </b-card-header>
          <b-card-body>
            <h5 class="mt-1 mb-1 text-body font-weight-bold">
              Create Sports League
            </h5>

            <b-form>
              <b-row>
                <b-col md="4">
                  <b-form-group>
                    <span class="font-weight-bold">Icon:</span>
                    <b-form-input
                      v-model="form.icon"
                      placeholder="Enter icon link..."
                    ></b-form-input>
                    <small class="text-primary">More Icons</small>
                  </b-form-group>
                </b-col>
                <b-col md="4">
                  <b-form-group>
                    <span class="font-weight-bold">Order:</span>
                    <b-form-input
                      v-model="form.order"
                      :formatter="formatPrice"
                    ></b-form-input>
                  </b-form-group>
                </b-col>
                <b-col md="4">
                  <b-form-group>
                    <span class="font-weight-bold">Tier:</span>
                    <b-form-select
                      v-model="form.tier"
                      :options="tierOptions"
                    ></b-form-select>
                  </b-form-group>
                </b-col>
              </b-row>

              <!-- <b-form-group>
                <span class="font-weight-bold">League Code:</span>
                <b-form-input
                  v-model="form.code"
                  placeholder="Enter league code"
                ></b-form-input>
              </b-form-group> -->

              <b-form-group>
                <label class="font-weight-bold text-warning">Name:</label>
                <b-form-input
                  v-model="form.name"
                  placeholder="Enter league name (e.g., AFL, NRL, NBA)"
                ></b-form-input>
              </b-form-group>

              <b-form-group>
                <span class="font-weight-bold">Short Name:</span>
                <b-form-input
                  v-model="form.shortName"
                  placeholder="Short version (e.g., AFL, NRL)"
                ></b-form-input>
              </b-form-group>

              <b-form-group>
                <span class="font-weight-bold">Official Name:</span>
                <b-form-input
                  v-model="form.officialName"
                  placeholder="Official league name"
                ></b-form-input>
              </b-form-group>

              <b-form-group>
                <span class="font-weight-bold">Description:</span>
                <b-form-textarea
                  v-model="form.description"
                  placeholder="League description and additional details"
                ></b-form-textarea>
              </b-form-group>

              <b-row>
                <b-col md="6">
                  <b-form-group>
                    <span class="font-weight-bold">Display:</span>
                    <b-form-radio-group
                      v-model="form.display"
                      :options="yesNoOptions"
                      name="display-options"
                      class="mt-1"
                    ></b-form-radio-group>
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group>
                    <span class="font-weight-bold">Featured League:</span>
                    <b-form-radio-group
                      v-model="form.featured"
                      :options="yesNoOptions"
                      name="featured-options"
                      class="mt-1"
                    ></b-form-radio-group>
                  </b-form-group>
                </b-col>
              </b-row>
            </b-form>
          </b-card-body>
        </b-card>

        <b-card no-body class="mb-4">
          <b-card-header class="bg-dark text-white p-1">
            League Details
          </b-card-header>
          <b-card-body>
            <b-row class="mt-2">
              <b-col md="6">
                <b-form-group>
                  <p class="font-weight-bold mb-0">
                    Country: <span class="text-danger">*</span>
                  </p>
                  <b-form-select
                    v-model="form.country"
                    :options="countryOptions"
                  ></b-form-select>
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group>
                  <span class="font-weight-bold">Region:</span>
                  <b-form-select
                    v-model="form.region"
                    :options="regionOptions"
                  ></b-form-select>
                </b-form-group>
              </b-col>
            </b-row>

            <b-row>
              <b-col md="6">
                <b-form-group>
                  <p class="font-weight-bold mb-0">
                    Season: <span class="text-danger">*</span>
                  </p>
                  <b-form-input
                    v-model="form.season"
                    placeholder="2024/25"
                  ></b-form-input>
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group>
                  <span class="font-weight-bold">Competition Type:</span>
                  <b-form-select
                    v-model="form.competitionType"
                    :options="competitionOptions"
                  ></b-form-select>
                </b-form-group>
              </b-col>
            </b-row>

            <b-row>
              <b-col md="6">
                <b-form-group>
                  <span class="font-weight-bold">Max Teams:</span>
                  <b-form-input
                    v-model="form.maxTeam"
                    type="number"
                  ></b-form-input>
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group>
                  <span class="font-weight-bold">Status:</span>
                  <b-form-select
                    v-model="form.status"
                    :options="statusOptions"
                  ></b-form-select>
                </b-form-group>
              </b-col>
            </b-row>

            <b-row>
              <b-col md="6">
                <b-form-group>
                  <span class="font-weight-bold">Season Start Date:</span>
                  <flat-pickr
                    id="startDate"
                    v-model="form.seasonStartDate"
                    class="form-control"
                    :config="configDate"
                    placeholder="mm/dd/yyyy"
                  />
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group>
                  <span class="font-weight-bold">Season End Date:</span>
                  <flat-pickr
                    id="startDate"
                    v-model="form.seasonEndDate"
                    class="form-control"
                    :config="configDate"
                    placeholder="mm/dd/yyyy"
                  />
                </b-form-group>
              </b-col>

              <!-- League Configuration -->
            </b-row>
          </b-card-body>
        </b-card>
      </b-col>

      <b-col cols="12">
        <b-card no-body class="mb-4">
          <b-card-header
            class="text-white p-1 mb-1"
            style="background-color: #007bff"
          >
            League Configuration
          </b-card-header>
          <b-card-body>
            <b-row>
              <b-col md="6">
                <b-form-group>
                  <span class="font-weight-bold">Timezone:</span>
                  <b-form-select
                    v-model="formLeague.timeZone"
                    :options="timezoneOptions"
                  ></b-form-select>
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group>
                  <span class="font-weight-bold">Currency:</span>
                  <b-form-select
                    v-model="formLeague.currency"
                    :options="currencyOptions"
                  ></b-form-select>
                </b-form-group>
              </b-col>
            </b-row>

            <b-row>
              <b-col md="6">
                <b-form-group>
                  <span class="font-weight-bold">Official Website:</span>
                  <b-form-input
                    v-model="formLeague.officalWebsite"
                    placeholder="https://www.league-website.com"
                  ></b-form-input>
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group>
                  <span class="font-weight-bold">Governing Body:</span>
                  <b-form-input
                    v-model="formLeague.governingBody"
                    placeholder="e.g., AFL Commission, NRL"
                  ></b-form-input>
                </b-form-group>
              </b-col>
            </b-row>

            <b-form-group>
              <span class="font-weight-bold">Sponsors/Partners:</span>
              <b-form-textarea
                v-model="formLeague.sponspors"
                placeholder="List major sponsors and partners"
                rows="2"
              ></b-form-textarea>
            </b-form-group>

            <b-form-group>
              <span class="font-weight-bold">Broadcast Rights:</span>
              <b-form-input
                v-model="formLeague.broadcastRights"
                placeholder="e.g., Fox Sports, ESPN, Sky Sports"
              ></b-form-input>
            </b-form-group>
          </b-card-body>
        </b-card>
      </b-col>

      <b-col cols="12">
        <b-card no-body>
          <b-card-header
            class="text-white p-1"
            style="background-color: #9c27b0"
          >
            Default League Settings
          </b-card-header>
          <b-card-body>
            <b-row class="mt-2">
              <b-col
                v-for="(option, index) in defaultSettingsOptions"
                :key="index"
                md="4"
                class="mt-1"
              >
                <b-form-checkbox
                  v-model="formLeague.defaultSettings"
                  :value="option.value"
                >
                  {{ option.text }}
                </b-form-checkbox>
              </b-col>
            </b-row>
          </b-card-body>
        </b-card>
      </b-col>

      <b-col cols="12">
        <div class="d-flex justify-content-end">
          <b-button variant="secondary" class="mr-1" @click="handleBack"
            >Cancel</b-button
          >
          <template>
            <b-button
              v-if="!$route.params.leagueId"
              @click="handleSubmitCreateLeague"
              variant="success"
              :disabled="loadingCreateLeague"
            >
              <Spinner v-if="loadingCreateLeague" />
              <template v-else> Create League </template>
            </b-button>
            <b-button
              v-else
              @click="handleSubmitUpdateLeague"
              variant="success"
              :disabled="loadingCreateLeague"
            >
              <Spinner v-if="loadingCreateLeague" />
              <template v-else> Save Changes </template>
            </b-button>
          </template>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BButton,
  BCard,
  BCardHeader,
  BCardBody,
  BForm,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BFormTextarea,
  BRow,
  BCol,
  BFormRadioGroup,
  BFormCheckboxGroup,
  BFormCheckbox,
} from "bootstrap-vue";
import flatPickr from "vue-flatpickr-component";
import { mapActions, mapState } from "vuex";
import Spinner from "@/@core/components/Spinner.vue";
import useToastHandler from "@/services/toastHandler";

export default {
  components: {
    BButton,
    BCard,
    BCardHeader,
    BCardBody,
    BForm,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BFormTextarea,
    BRow,
    BCol,
    BFormRadioGroup,
    BFormCheckboxGroup,
    BFormCheckbox,
    flatPickr,
    Spinner,
  },
  data() {
    return {
      leagueItem: null,
      form: {
        icon: "",
        code: "",
        order: null,
        tier: null,
        name: "",
        shortName: "",
        officialName: "",
        description: "",
        display: false,
        featured: false,
        country: "",
        region: "",
        season: "",
        competitionType: "",
        maxTeam: null,
        seasonStartDate: null,
        seasonEndDate: null,
      },
      yesNoOptions: [
        { value: false, text: "No" },
        { value: true, text: "Yes" },
      ],
      iconOptions: [
        { value: "🏆", text: "🏆 Default League" },
        { value: "🏀", text: "🏀 Basketball" },
        { value: "⚽", text: "⚽ Football" },
      ],
      tierOptions: [
        { value: 1, text: "Tier 1" },
        { value: 2, text: "Tier 2" },
        { value: 3, text: "Tier 3" },
      ],
      countryOptions: [
        { value: "Australia", text: "Australia" },
        { value: "New Zealand", text: "New Zealand" },
        { value: "United States", text: "United States" },
        { value: "United Kingdom", text: "United Kingdom" },
        { value: "Canada", text: "Canada" },
      ],
      regionOptions: [
        { value: "Australia", text: "Australia" },
        { value: "Oceania", text: "Oceania" },
        { value: "North America", text: "North America" },
        { value: "Europe", text: "Europe" },
        { value: "Asia", text: "Asia" },
      ],
      competitionOptions: [
        { value: "League", text: "League" },
        { value: "Cup", text: "Cup" },
        { value: "Tournament", text: "Tournament" },
        { value: "Championship", text: "Championship" },
        { value: "Series", text: "Series" },
      ],
      statusOptions: [
        { value: "Active", text: "Active" },
        { value: "Inactive", text: "Inactive" },
      ],
      configDate: {
        // dateFormat: 'd-m-Y',
        altFormat: "d-m-Y",
        altInput: true,
        static: true,
      },
      timezoneOptions: [
        { value: "AEST", text: "AEST (Australian Eastern)" },
        { value: "GMT", text: "GMT" },
        { value: "UTC", text: "UTC" },
      ],
      currencyOptions: [
        { value: "AUD", text: "AUD (Australian Dollar)" },
        { value: "USD", text: "USD (US Dollar)" },
        { value: "EUR", text: "EUR (Euro)" },
      ],
      defaultSettingsOptions: [
        { value: "allowDraw", text: "Allow Draws" },
        { value: "allowOverTime", text: "Overtime/Extra Time" },
        { value: "isPlayoff", text: "Playoffs/Finals" },
        { value: "allowLiveBetting", text: "Live Betting" },
        { value: "allowCashOut", text: "Cash Out" },
        { value: "allowLiveStreaming", text: "Live Streaming" },
      ],
      formLeague: {
        timeZone: "AEST",
        currency: "AUD",
        officalWebsite: "",
        governingBody: "",
        sponspors: "",
        broadcastRights: "",
        defaultSettings: [],
      },
      selectedSport: null,
    };
  },
  async created() {
    await this.fetchDetailSports();
    if (!this.$route.params.leagueId) return;
    const res = await this.getListLeagueOfSport({
      sportId: this.$route.params.id,
      limit: 1,
      id: this.$route.params.leagueId,
    });
    if (res.data) {
      const leagueItem = res.data[0];

      const listDefaultSetting = [];
      if (leagueItem.allowDraw) listDefaultSetting.push("allowDraw");
      if (leagueItem.allowOverTime) listDefaultSetting.push("allowOverTime");
      if (leagueItem.isPlayoff) listDefaultSetting.push("isPlayoff");
      if (leagueItem.allowLiveBetting)
        listDefaultSetting.push("allowLiveBetting");
      if (leagueItem.allowCashOut) listDefaultSetting.push("allowCashOut");
      if (leagueItem.allowLiveStreaming)
        listDefaultSetting.push("allowLiveStreaming");
      this.leagueItem = leagueItem;
      this.form = {
        ...this.form,
        tier: leagueItem.tier,
        code: leagueItem.code,
        icon: leagueItem.logo,
        order: leagueItem.orderNumber,
        name: leagueItem.name,
        shortName: leagueItem.shortName,
        officialName: leagueItem.officialName,
        description: leagueItem.description,
        display: leagueItem.status === "active",
        featured: !!leagueItem.isLeagueFeatured,
        country: leagueItem.country,
        region: leagueItem.region,
        season: leagueItem.season,
        competitionType: leagueItem.competitionType,
        maxTeam: leagueItem.maxTeam,
        seasonStartDate: leagueItem.seasonStartDate,
        seasonEndDate: leagueItem.seasonEndDate,
      };

      this.formLeague = {
        ...this.formLeague,
        timeZone: leagueItem.timeZone,
        currency: leagueItem.currency,
        officalWebsite: leagueItem.officalWebsite,
        governingBody: leagueItem.governingBody,
        sponspors: leagueItem.sponspors,
        broadcastRights: leagueItem.broadcastRights,
        defaultSettings: listDefaultSetting,
      };
    }
  },
  computed: {
    ...mapState("sport/create", ["loadingCreateLeague"]),
  },
  methods: {
    ...mapActions({
      createLeague: "sport/create/createLeague",
      udpdateLeague: "sport/create/udpdateLeague",
      getListLeagueOfSport: "sport/list/getListLeagueOfSport",
      getListSport: "sport/list/getListSport",
    }),
    async fetchDetailSports() {
      const data = await this.getListSport({
        limit: 1,
        page: 1,
        id: this.$route.params.id,
      });
      if (!data && data.data.length <= 0)
        return this.showToastError(false, "Error fetching sports list");
      if (Array.isArray(data.data)) {
        this.selectedSport = data.data[0];
      }
    },
    formatPrice(value) {
      value = value.replace(/[^0-9.]/g, "");

      const parts = value.split(".");
      if (parts.length > 2) {
        value = parts[0] + "." + parts.slice(1).join("");
      }

      if (value.includes(".")) {
        const decimalPart = value.split(".")[1];
        if (decimalPart.length > 2) {
          const integerPart = value.split(".")[0];
          value = `${integerPart}.${decimalPart.slice(0, 2)}`;
        }
      }

      return value;
    },
    handleBack() {
      this.resetData();
      if (this.$route.params.id && this.$route.params.code) {
        return this.$router.push(
          `/trading/sports/${this.$route.params.code}/${this.$route.params.id}`
        );
      }
      this.$router.push({ name: "trading-sports" });
    },
    async handleSubmitCreateLeague() {
      const res = await this.createLeague({
        name: this.form.name,
        shortName: this.form.shortName,
        officialName: this.form.officialName,
        description: this.form.description,
        code: this.selectedSport.code,
        logo: this.form.icon,
        orderNumber: Number(this.form.order),
        tier: Number(this.form.tier),
        source: "manual",
        status: this.form.display ? "active" : "disabled",
        sportId: this.$route.params.id,
        isLeagueFeatured: this.form.featured,
        sportLeagueAdvance: {
          country: this.form.country,
          region: this.form.region,
          season: this.form.season,
          competitionType: this.form.competitionType,
          maxTeam: Number(this.form.maxTeam),
          seasonStartDate: this.form.seasonStartDate,
          seasonEndDate: this.form.seasonEndDate,
          timeZone: this.formLeague.timeZone,
          currency: this.formLeague.currency,
          officalWebsite: this.formLeague.officalWebsite,
          governingBody: this.formLeague.governingBody,
          sponspors: this.formLeague.sponspors,
          broadcastRights: this.formLeague.broadcastRights,
          allowLiveBetting: this.formLeague.defaultSettings.some(
            (i) => i === "allowLiveBetting"
          ),
          allowCashOut: this.formLeague.defaultSettings.some(
            (i) => i === "allowCashOut"
          ),
          allowLiveStreaming: this.formLeague.defaultSettings.some(
            (i) => i === "allowLiveStreaming"
          ),
          allowDraw: this.formLeague.defaultSettings.some(
            (i) => i === "allowDraw"
          ),
          allowOverTime: this.formLeague.defaultSettings.some(
            (i) => i === "allowOverTime"
          ),
          isPlayoff: this.formLeague.defaultSettings.some(
            (i) => i === "isPlayoff"
          ),
        },
      });
      if (res) {
        this.showToastSuccess(true, "New league created successfully!");
        this.handleBack();
      }
    },
    async handleSubmitUpdateLeague() {
      const res = await this.udpdateLeague({
        id: this.$route.params.leagueId,
        name: this.form.name,
        shortName: this.form.shortName,
        officialName: this.form.officialName,
        description: this.form.description,
        code: this.form.code,
        logo: this.form.icon,
        orderNumber: Number(this.form.order),
        tier: Number(this.form.tier),
        source: this.leagueItem.source,
        status: this.form.display ? "active" : "disabled",
        sportId: this.$route.params.id,
        isLeagueFeatured: this.form.featured,
        sportLeagueAdvance: {
          country: this.form.country,
          region: this.form.region,
          season: this.form.season,
          competitionType: this.form.competitionType,
          maxTeam: Number(this.form.maxTeam),
          seasonStartDate: this.form.seasonStartDate,
          seasonEndDate: this.form.seasonEndDate,
          timeZone: this.formLeague.timeZone,
          currency: this.formLeague.currency,
          officalWebsite: this.formLeague.officalWebsite,
          governingBody: this.formLeague.governingBody,
          sponspors: this.formLeague.sponspors,
          broadcastRights: this.formLeague.broadcastRights,
          allowLiveBetting: this.formLeague.defaultSettings.some(
            (i) => i === "allowLiveBetting"
          ),
          allowCashOut: this.formLeague.defaultSettings.some(
            (i) => i === "allowCashOut"
          ),
          allowLiveStreaming: this.formLeague.defaultSettings.some(
            (i) => i === "allowLiveStreaming"
          ),
          allowDraw: this.formLeague.defaultSettings.some(
            (i) => i === "allowDraw"
          ),
          allowOverTime: this.formLeague.defaultSettings.some(
            (i) => i === "allowOverTime"
          ),
          isPlayoff: this.formLeague.defaultSettings.some(
            (i) => i === "isPlayoff"
          ),
        },
      });
      if (res) {
        this.showToastSuccess(true, "The league updated successfully!");
        this.handleBack();
      }
    },
    resetData() {
      this.leagueItem = null;
      this.form = {
        icon: "🏆",
        code: "",
        order: 0,
        tier: null,
        name: "",
        shortName: "",
        officialName: "",
        description: "",
        display: false,
        featured: false,
        country: "",
        region: "",
        season: "",
        competitionType: "",
        maxTeam: null,
        seasonStartDate: null,
        seasonEndDate: null,
      };
      this.formLeague = {
        timeZone: "AEST",
        currency: "AUD",
        officalWebsite: "",
        governingBody: "",
        sponspors: "",
        broadcastRights: "",
        defaultSettings: [],
      };
    },
  },
  setup() {
    const { showToastError, showToastSuccess } = useToastHandler();
    return {
      showToastError,
      showToastSuccess,
    };
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";
</style>

<style lang="scss" scoped>
.container-league {
  max-width: 1024px;
  margin: auto;
  padding: 16px;
}
</style>
